  import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stockApi, productsApi, ordersApi } from '../api/client';
import Input from '../components/Input';
import Button from '../components/Button';
import Card from '../components/Card';
import { parseQrCode } from '../utils/qrParser';

// Функция форматирования количества: целые числа без десятичной части, дробные с одним знаком
const formatQty = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toString() : num.toFixed(1);
};

// Функция форматирования номера телефона в формате Казахстана: +7 (XXX) XXX-XX-XX
const formatPhoneNumber = (value) => {
  // Удаляем все нецифровые символы, кроме +
  const phoneNumber = value.replace(/[^\d+]/g, '');
  
  // Если номер начинается не с +7, добавляем +7
  let digits = phoneNumber.startsWith('+7') 
    ? phoneNumber.substring(2).replace(/\D/g, '')
    : phoneNumber.replace(/\D/g, '');
  
  // Ограничиваем до 10 цифр (без +7)
  digits = digits.substring(0, 10);
  
  // Форматируем: +7 (XXX) XXX-XX-XX
  if (digits.length === 0) {
    return '';
  }
  
  let formatted = '+7';
  if (digits.length > 0) {
    formatted += ' (' + digits.substring(0, 3);
  }
  if (digits.length >= 4) {
    formatted += ') ' + digits.substring(3, 6);
  }
  if (digits.length >= 7) {
    formatted += '-' + digits.substring(6, 8);
  }
  if (digits.length >= 9) {
    formatted += '-' + digits.substring(8, 10);
  }
  
  return formatted;
};

// Функция для получения чистого номера телефона (только цифры после +7)
const getCleanPhoneNumber = (value) => {
  const phoneNumber = value.replace(/[^\d+]/g, '');
  if (phoneNumber.startsWith('+7')) {
    return phoneNumber.substring(2).replace(/\D/g, '');
  }
  return phoneNumber.replace(/\D/g, '');
};

const Sales = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState(searchParams.get('product_id') || null);
  const [qty, setQty] = useState('');
  const [product, setProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [isPreorder, setIsPreorder] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dioptry, setDioptry] = useState(''); // Диоптрии для текущего товара

  // Варианты диоптрий
  const dioptryOptions = [];
  dioptryOptions.push(0, 1.00, 1.25, 1.50, 1.75);
  for (let i = 2.00; i <= 10.00; i += 0.25) {
    dioptryOptions.push(parseFloat(i.toFixed(2)));
  }
  // for (let i = 5.50; i <= 9.00; i += 0.50) {
  //   dioptryOptions.push(parseFloat(i.toFixed(2)));
  // }
  
  const [customerData, setCustomerData] = useState({
    customer_name: '',
    instagram: '',
    phone: '',
    delivery_address: '',
  });

  const { data: productData } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const response = await productsApi.getById(productId);
      return response.data;
    },
    enabled: !!productId,
  });

  const { data: stockData } = useQuery({
    queryKey: ['stock', productId],
    queryFn: async () => {
      const response = await stockApi.getStock(productId);
      return response.data;
    },
    enabled: !!productId,
  });

  // Загружаем все товары для поиска
  const { data: allProducts } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await productsApi.getAll();
      return response.data;
    },
  });

  useEffect(() => {
    if (productData) {
      setProduct(productData);
    }
  }, [productData]);

  // Фильтрация товаров по поисковому запросу
  const filteredProducts = useMemo(() => {
    if (!allProducts || !searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase().trim();
    
    return allProducts.filter(product => {
      const nameMatch = product.name?.toLowerCase().includes(query);
      const itemCodeMatch = product.item_code?.toLowerCase().includes(query);
      
      return nameMatch || itemCodeMatch;
    }).slice(0, 20); // Ограничиваем до 20 результатов для производительности
  }, [allProducts, searchQuery]);



  const handleSelectProduct = (selectedProduct) => {
    setProduct(selectedProduct);
    setProductId(selectedProduct.id);
    setSearchQuery('');
  };

  const addToCart = () => {
    const qtyValue = parseFloat(qty);
    if (!product || !qty || qtyValue <= 0) {
      alert('Выберите товар и введите количество');
      return;
    }

    const currentStock = stockData?.total_stock || stockData?.stock || 0;
    
    // Проверяем остаток только если не предзаказ
    if (!isPreorder && currentStock < qtyValue) {
      alert(`Недостаточно товара. Доступно: ${formatQty(currentStock)}`);
      return;
    }

    // Проверяем остаток по диоптриям, если диоптрии указаны
    const dioptryValue = dioptry && dioptry.trim() !== '' ? parseFloat(dioptry) : null;
    if (!isPreorder && dioptryValue !== null && stockData?.stock_by_dioptry) {
      const dioptryStock = stockData.stock_by_dioptry.find(
        item => item.dioptry === dioptryValue
      );
      if (!dioptryStock || dioptryStock.stock < qtyValue) {
        alert(`Недостаточно товара с диоптриями ${dioptryValue === 0 ? '0' : dioptryValue.toFixed(2)}. Доступно: ${formatQty(dioptryStock?.stock || 0)}`);
        return;
      }
    }

    // Добавляем товар в корзину (каждый товар с диоптриями - отдельная позиция)
    setCart([...cart, {
      product_id: product.id,
      product_name: product.name,
      qty: qtyValue,
      price: parseFloat(product.price),
      dioptry: dioptryValue,
    }]);

    // Очищаем форму
    setQty('');
    setDioptry('');
    setProduct(null);
    setProductId(null);
  };

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
  };

  const updateCartQty = (index, newQty) => {
    const qtyValue = parseFloat(newQty);
    if (qtyValue <= 0) {
      removeFromCart(index);
      return;
    }
    const newCart = [...cart];
    newCart[index].qty = qtyValue;
    setCart(newCart);
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
  };

  const orderMutation = useMutation({
    mutationFn: (data) => ordersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['stock']);
      queryClient.invalidateQueries(['products']);
      alert(isPreorder ? 'Предзаказ успешно создан!' : 'Продажа успешно завершена!');
      setCart([]);
      setCustomerData({ customer_name: '', instagram: '', phone: '', delivery_address: '' });
      setShowCustomerForm(false);
      setIsPreorder(false);
      setProduct(null);
      setProductId(null);
      setQty('');
    },
  });

  const handlePhoneChange = async (e) => {
    const inputValue = e.target.value;
    const formattedPhone = formatPhoneNumber(inputValue);
    setCustomerData({ ...customerData, phone: formattedPhone });

    // Если номер телефона достаточно длинный (минимум 7 цифр после +7), ищем заказ
    const cleanPhone = getCleanPhoneNumber(formattedPhone);
    if (cleanPhone.length >= 7) {
      try {
        // Ищем по отформатированному номеру
        const response = await ordersApi.getByPhone(formattedPhone);
        const orderData = response.data;
        
        // Заполняем поля данными из найденного заказа
        setCustomerData({
          customer_name: orderData.customer_name || customerData.customer_name,
          instagram: orderData.instagram || customerData.instagram,
          phone: formattedPhone,
          delivery_address: orderData.delivery_address || customerData.delivery_address,
        });
      } catch (error) {
        // Если заказ не найден, пробуем поиск по чистому номеру
        if (error.response?.status === 404 && cleanPhone.length === 10) {
          try {
            const cleanPhoneFormatted = '+7 (' + cleanPhone.substring(0, 3) + ') ' + 
                                       cleanPhone.substring(3, 6) + '-' + 
                                       cleanPhone.substring(6, 8) + '-' + 
                                       cleanPhone.substring(8, 10);
            const response = await ordersApi.getByPhone(cleanPhoneFormatted);
            const orderData = response.data;
            
            setCustomerData({
              customer_name: orderData.customer_name || customerData.customer_name,
              instagram: orderData.instagram || customerData.instagram,
              phone: formattedPhone,
              delivery_address: orderData.delivery_address || customerData.delivery_address,
            });
          } catch (err) {
            // Если заказ не найден, просто продолжаем без автозаполнения
            if (err.response?.status !== 404) {
              console.error('Ошибка поиска заказа по телефону:', err);
            }
          }
        } else if (error.response?.status !== 404) {
          console.error('Ошибка поиска заказа по телефону:', error);
        }
      }
    }
  };

  const handleSubmitOrder = (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      alert('Добавьте товары в корзину');
      return;
    }

    if (!customerData.customer_name.trim()) {
      alert('Введите имя покупателя');
      return;
    }

    const orderData = {
      items: cart.map(item => ({
        product_id: item.product_id,
        qty: parseFloat(item.qty),
        price: item.price,
        dioptry: item.dioptry !== null && item.dioptry !== undefined ? item.dioptry : null,
      })),
      customer_name: customerData.customer_name.trim(),
      instagram: customerData.instagram.trim() || null,
      phone: customerData.phone.trim() || null,
      delivery_address: customerData.delivery_address.trim() || null,
      is_preorder: isPreorder,
    };

    orderMutation.mutate(orderData);
  };

  const currentStock = stockData?.total_stock || stockData?.stock || 0;

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Продажа</h1>

      {!showCustomerForm ? (
        <>
          {/* Добавление товара */}
          {product ? (
            <Card className="mb-4">
              <h3 className="text-lg font-semibold mb-2">{product.name}</h3>
              {product.item_code && (
                <p className="text-sm font-bold text-blue-600 mb-1 font-mono">
                  Артикул: {product.item_code}
                </p>
              )}
              <p className="text-sm text-gray-500">QR: {product.qr_code}</p>
              <p className="text-lg font-bold text-blue-600 mt-2">
                {parseFloat(product.price).toFixed(2)} ₸
              </p>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Доступно: <span className="font-bold text-gray-900">{formatQty(currentStock)}</span>
                </p>
              </div>
            </Card>
          ) : (
            <div className="mb-4">
              <div className="mb-3 relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="🔍 Поиск по названию или артикулу..."
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
                    aria-label="Очистить поиск"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              {/* Список результатов поиска */}
              {searchQuery.trim() && filteredProducts.length > 0 && (
                <Card className="mb-4 max-h-96 overflow-y-auto">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-semibold text-gray-700">
                      Найдено товаров: {filteredProducts.length}
                    </h4>
                    {filteredProducts.length === 20 && (
                      <span className="text-xs text-gray-500">
                        Показано первые 20
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {filteredProducts.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleSelectProduct(result)}
                        className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{result.name}</p>
                            {result.item_code && (
                              <p className="text-xs text-blue-600 font-mono mt-1">
                                Артикул: {result.item_code}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 mt-1">
                              Цена: {parseFloat(result.price || 0).toFixed(2)} ₸
                            </p>
                          </div>
                          <div className="text-right ml-2">
                            <p className={`text-xs font-semibold ${
                              (result.stock || 0) > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatQty(result.stock || 0)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              {searchQuery.trim() && filteredProducts.length === 0 && (
                <Card className="mb-4 p-4">
                  <p className="text-center text-gray-500 text-sm">
                    Товары не найдены
                  </p>
                  <p className="text-center text-xs text-gray-400 mt-1">
                    По запросу "{searchQuery}" ничего не найдено
                  </p>
                </Card>
              )}

              {!searchQuery.trim() && (
                <Card className="mb-4 p-4">
                  <p className="text-center text-gray-500 text-sm">
                    Введите название или артикул товара для поиска
                  </p>
                </Card>
              )}
            </div>
          )}

              {product && (
                <div className="mb-4">
                  <div className="mb-4 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="preorder"
                      checked={isPreorder}
                      onChange={(e) => setIsPreorder(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="preorder" className="text-sm font-medium text-gray-700">
                      Предзаказ (товар может отсутствовать на складе)
                    </label>
                  </div>

                  <Input
                    label="Количество"
                    type="number"
                    step="0.1"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    placeholder="Введите количество"
                    required
                    min="0.1"
                    max={isPreorder ? undefined : currentStock}
                  />

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Диоптрии (необязательно)
                    </label>
                    <select
                      value={dioptry}
                      onChange={(e) => setDioptry(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base"
                    >
                      <option value="">Не указано</option>
                      {dioptryOptions.map((value) => (
                        <option key={value} value={value}>
                          {value === 0 ? '0' : "-" + value.toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Button
                    variant="success"
                    size="lg"
                    className="w-full mt-2"
                    onClick={addToCart}
                    disabled={!productId || !qty}
                  >
                    ➕ Добавить в корзину
                  </Button>

                  {product && (
                    <Button
                      variant="secondary"
                      size="md"
                      className="w-full mt-2"
                      onClick={() => {
                        setProduct(null);
                        setProductId(null);
                        setQty('');
                        setDioptry('');
                      }}
                    >
                      Изменить товар
                    </Button>
                  )}
                </div>
              )}

              {/* Корзина */}
              {cart.length > 0 && (
                <Card className="mb-4">
                  <h3 className="text-lg font-semibold mb-3">Корзина</h3>
                  <div className="space-y-2">
                    {cart.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.product_name}</p>
                          {item.dioptry !== null && item.dioptry !== undefined && (
                            <p className="text-xs text-blue-600 font-medium mt-1">
                              Диоптрии: {item.dioptry === 0 ? '0' : item.dioptry.toFixed(2)}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="number"
                              step="0.1"
                              value={item.qty}
                              onChange={(e) => updateCartQty(index, e.target.value)}
                              min="0.1"
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <span className="text-sm text-gray-600">× {item.price.toFixed(2)} ₸</span>
                            <span className="text-sm font-bold text-blue-600">
                              = {(item.price * item.qty).toFixed(2)} ₸
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFromCart(index)}
                          className="text-red-600 hover:text-red-800 ml-2"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Итого:</span>
                      <span className="text-xl font-bold text-blue-600">
                        {calculateTotal().toFixed(2)} ₸
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full mt-3"
                    onClick={() => setShowCustomerForm(true)}
                  >
                    Оформить заказ
                  </Button>
                </Card>
              )}
            </>
          ) : (
            <>
              {/* Форма данных покупателя */}
              <Card className="mb-4">
                <h3 className="text-lg font-semibold mb-3">Данные покупателя</h3>
                <form onSubmit={handleSubmitOrder}>
                  <Input
                    label="Имя покупателя *"
                    value={customerData.customer_name}
                    onChange={(e) => setCustomerData({ ...customerData, customer_name: e.target.value })}
                    placeholder="Введите имя"
                    required
                  />

                  <Input
                    label="Инстаграм"
                    value={customerData.instagram}
                    onChange={(e) => setCustomerData({ ...customerData, instagram: e.target.value })}
                    placeholder="@username"
                  />

                  <Input
                    label="Номер телефона"
                    type="tel"
                    value={customerData.phone}
                    onChange={handlePhoneChange}
                    placeholder="+7 (XXX) XXX-XX-XX"
                    maxLength={18}
                  />

                  <Input
                    label="Адрес получателя"
                    value={customerData.delivery_address}
                    onChange={(e) => setCustomerData({ ...customerData, delivery_address: e.target.value })}
                    placeholder="Необязательно"
                  />

                  <div className="mb-4 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="preorder_final"
                      checked={isPreorder}
                      onChange={(e) => setIsPreorder(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="preorder_final" className="text-sm font-medium text-gray-700">
                      Предзаказ
                    </label>
                  </div>

                  {/* Сводка заказа */}
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Состав заказа:</p>
                    {cart.map((item, index) => (
                      <div key={index} className="text-sm text-gray-600 mb-1">
                        {item.product_name}
                        {item.dioptry !== null && item.dioptry !== undefined && (
                          <span className="text-blue-600"> (диоптрии: {item.dioptry === 0 ? '0' : item.dioptry.toFixed(2)})</span>
                        )}
                        {' '}× {formatQty(item.qty)} = {(item.price * item.qty).toFixed(2)} ₸
                      </div>
                    ))}
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-900">Общая сумма:</span>
                        <span className="font-bold text-blue-600">
                          {calculateTotal().toFixed(2)} ₸
                        </span>
                      </div>
                    </div>
                  </div>

                  {orderMutation.error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                      {orderMutation.error.response?.data?.error || 'Произошла ошибка'}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="lg"
                      className="flex-1"
                      onClick={() => setShowCustomerForm(false)}
                    >
                      Назад
                    </Button>
                    <Button
                      type="submit"
                      variant="success"
                      size="lg"
                      className="flex-1"
                      disabled={orderMutation.isPending}
                    >
                      {orderMutation.isPending ? 'Обработка...' : isPreorder ? 'Создать предзаказ' : 'Завершить продажу'}
                    </Button>
                  </div>
                </form>
              </Card>
            </>
          )}
    </div>
  );
};

export default Sales;
